import {
  chapter_chapterId,
  chapterId,
  id,
  label,
  note,
  position,
  quote,
  readingCompensation,
  readingCompleted,
  readingCompletedTimestamp,
  readingContent,
  readingDateLabel,
  readingEntryCompleted,
  readingEntryId,
  readingId,
  readingPlanId,
  readingSequence,
  settingsJson,
  timestamp,
  userId,
  verseRange,
} from '../constants/stringsAndFields';
import {
  getFavoriteQuotes,
  insertFavoriteQuote,
} from '../database/favoriteQuote';
import {getNotes, insertNote} from '../database/note';
import {
  getReadingPlanProgresses,
  insertReadingPlanProgress,
} from '../database/readingPlanProgress';
import {getUserSettingsRow, upsertUserSettings} from '../database/userSettings';
import {exportDatabase} from '../database/sqlOperations';
import {migrateAsyncStorageToUserSettingsDb} from './userSettingsSync';

function mapNoteToRemote(n, uid) {
  return {
    id: n[id],
    user_id: uid,
    chapter_id: n[chapterId] ?? null,
    chapter_chapter_id: n[chapter_chapterId] ?? null,
    note: n[note],
    label: n[label] ?? null,
    position: n[position] ?? null,
    timestamp: n[timestamp] ?? null,
    verse_range: n[verseRange] ?? null,
  };
}

function mapRemoteToNote(r, uid) {
  return {
    [id]: r.id,
    [userId]: uid,
    [chapterId]: r.chapter_id,
    [chapter_chapterId]: r.chapter_chapter_id,
    [note]: r.note,
    [label]: r.label,
    [position]: r.position,
    [timestamp]: r.timestamp,
    [verseRange]: r.verse_range,
  };
}

function mapFavoriteToRemote(f, uid) {
  return {
    id: f[id],
    user_id: uid,
    chapter_chapter_id: f[chapter_chapterId] ?? null,
    quote: f[quote],
    label: f[label] ?? null,
    position: f[position] ?? null,
    timestamp: f[timestamp] ?? null,
    verse_range: f[verseRange] ?? null,
  };
}

function mapRemoteToFavorite(r, uid) {
  return {
    [id]: r.id,
    [userId]: uid,
    [chapter_chapterId]: r.chapter_chapter_id,
    [quote]: r.quote,
    [label]: r.label,
    [position]: r.position,
    [timestamp]: r.timestamp,
    [verseRange]: r.verse_range,
  };
}

/** For Postgres bigint: SQLite / export sometimes yield the string "null" instead of NULL. */
function coerceNullableBigint(value) {
  if (value == null || value === '' || value === 'null') {
    return null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? Math.trunc(value) : null;
  }
  const n = Number(value);
  return Number.isFinite(n) ? Math.trunc(n) : null;
}

function mapProgressToRemote(p, uid) {
  return {
    reading_id: p[readingId],
    user_id: uid,
    reading_plan_id: p[readingPlanId],
    reading_entry_id: p[readingEntryId],
    reading_content: p[readingContent],
    reading_sequence: p[readingSequence],
    reading_completed: p[readingCompleted] ? 1 : 0,
    reading_completed_timestamp: coerceNullableBigint(p[readingCompletedTimestamp]),
    reading_entry_completed: p[readingEntryCompleted] ? 1 : 0,
    reading_compensation: p[readingCompensation] ?? 0,
    reading_date_label: p[readingDateLabel] ?? null,
  };
}

function mapRemoteToProgress(r, uid) {
  return {
    [readingId]: r.reading_id,
    [userId]: uid,
    [readingPlanId]: r.reading_plan_id,
    [readingEntryId]: r.reading_entry_id,
    [readingContent]: r.reading_content,
    [readingSequence]: r.reading_sequence,
    [readingCompleted]: Boolean(r.reading_completed),
    [readingCompletedTimestamp]: coerceNullableBigint(r.reading_completed_timestamp),
    [readingEntryCompleted]: Boolean(r.reading_entry_completed),
    [readingCompensation]: r.reading_compensation,
    [readingDateLabel]: r.reading_date_label,
  };
}

async function pullNotes(supabase, db, uid) {
  const {data, error} = await supabase
    .from('user_notes')
    .select('*')
    .eq('user_id', uid);
  if (error) {
    console.warn('sync pull user_notes', error.message);
    return;
  }
  for (const row of data || []) {
    await insertNote(db, mapRemoteToNote(row, uid));
  }
}

async function pushNotes(supabase, db, uid) {
  const notes = await getNotes(db);
  if (!notes.length) {
    return;
  }
  const {error} = await supabase
    .from('user_notes')
    .upsert(notes.map(n => mapNoteToRemote(n, uid)), {onConflict: 'id'});
  if (error) {
    console.warn('sync push user_notes', error.message);
  }
}

async function pullFavorites(supabase, db, uid) {
  const {data, error} = await supabase
    .from('user_favorite_quotes')
    .select('*')
    .eq('user_id', uid);
  if (error) {
    console.warn('sync pull user_favorite_quotes', error.message);
    return;
  }
  for (const row of data || []) {
    await insertFavoriteQuote(db, mapRemoteToFavorite(row, uid));
  }
}

async function pushFavorites(supabase, db, uid) {
  const rows = await getFavoriteQuotes(db);
  if (!rows.length) {
    return;
  }
  const {error} = await supabase
    .from('user_favorite_quotes')
    .upsert(rows.map(f => mapFavoriteToRemote(f, uid)), {onConflict: 'id'});
  if (error) {
    console.warn('sync push user_favorite_quotes', error.message);
  }
}

/** PostgREST default max rows per request is 1000; paginate or completions beyond that never sync. */
const READING_PLAN_PROGRESS_PAGE_SIZE = 1000;

async function fetchAllReadingPlanProgressForUser(supabase, uid) {
  const all = [];
  let from = 0;
  for (;;) {
    const to = from + READING_PLAN_PROGRESS_PAGE_SIZE - 1;
    const {data, error} = await supabase
      .from('reading_plan_progress')
      .select('*')
      .eq('user_id', uid)
      .range(from, to);
    if (error) {
      return {rows: null, error};
    }
    const batch = data || [];
    all.push(...batch);
    if (batch.length < READING_PLAN_PROGRESS_PAGE_SIZE) {
      break;
    }
    from += READING_PLAN_PROGRESS_PAGE_SIZE;
  }
  return {rows: all, error: null};
}

/** @returns {Promise<boolean>} true if pull succeeded (rows may be empty); false on error */
export async function pullReadingPlanProgressFromRemote(supabase, db, uid) {
  const {rows: remote, error} = await fetchAllReadingPlanProgressForUser(
    supabase,
    uid,
  );
  if (error) {
    console.warn('sync pull reading_plan_progress', error.message);
    return false;
  }
  for (const row of remote) {
    await insertReadingPlanProgress(db, mapRemoteToProgress(row, uid));
  }
  return true;
}

async function pushProgress(supabase, db, uid) {
  const rows = await getReadingPlanProgresses(db);
  if (!rows.length) {
    return;
  }
  const {error} = await supabase
    .from('reading_plan_progress')
    .upsert(rows.map(p => mapProgressToRemote(p, uid)), {
      onConflict: 'reading_id,user_id',
    });
  if (error) {
    console.warn('sync push reading_plan_progress', error.message);
  }
}

async function pullSettings(supabase, db, uid) {
  const {data, error} = await supabase
    .from('user_settings')
    .select('*')
    .eq('user_id', uid)
    .maybeSingle();
  if (error) {
    console.warn('sync pull user_settings', error.message);
    return;
  }
  if (data?.settings != null) {
    await upsertUserSettings(db, {
      [userId]: uid,
      [settingsJson]: JSON.stringify(data.settings),
    });
  }
}

async function pushSettings(supabase, db, uid) {
  const row = await getUserSettingsRow(db, uid);
  if (!row?.[settingsJson]) {
    return;
  }
  let parsed;
  try {
    parsed = JSON.parse(row[settingsJson]);
  } catch {
    return;
  }
  const {error} = await supabase.from('user_settings').upsert(
    {
      user_id: uid,
      settings: parsed,
    },
    {onConflict: 'user_id'},
  );
  if (error) {
    console.warn('sync push user_settings', error.message);
  }
}

/**
 * Pull remote rows into SQLite, then push local state (last-write-wins on server for upserts).
 *
 * @param {object} [options]
 * @param {boolean} [options.skipPullReadingPlanProgress] — if true, do not pull reading plan progress here (caller already did).
 * @param {boolean|null} [options.readingPlanPullOk] — when skipPullReadingPlanProgress is true, whether the prior pull succeeded (controls push; avoids uploading zeros after a failed pull).
 */
export async function syncUserData(supabase, db, uid, options = {}) {
  if (!supabase || !db || !uid) {
    return;
  }
  const {
    skipPullReadingPlanProgress = false,
    readingPlanPullOk: priorReadingPlanPullOk = null,
  } = options;
  try {
    await pullSettings(supabase, db, uid);
    await pullNotes(supabase, db, uid);
    await pullFavorites(supabase, db, uid);
    let readingPlanPullOk = true;
    if (!skipPullReadingPlanProgress) {
      readingPlanPullOk = await pullReadingPlanProgressFromRemote(
        supabase,
        db,
        uid,
      );
    } else if (priorReadingPlanPullOk !== null) {
      readingPlanPullOk = priorReadingPlanPullOk;
    } else {
      readingPlanPullOk = false;
    }
    await migrateAsyncStorageToUserSettingsDb(db, uid);
    await pushSettings(supabase, db, uid);
    await pushNotes(supabase, db, uid);
    await pushFavorites(supabase, db, uid);
    if (readingPlanPullOk) {
      await pushProgress(supabase, db, uid);
    } else {
      console.warn(
        'syncUserData: skipping push reading_plan_progress (pull failed or unknown)',
      );
    }
    await exportDatabase(db);
  } catch (e) {
    console.warn('syncUserData', e);
  }
}

export async function syncUserSettingsOnly(supabase, db, uid) {
  if (!supabase || !db || !uid) {
    return;
  }
  try {
    await migrateAsyncStorageToUserSettingsDb(db, uid);
    await pushSettings(supabase, db, uid);
    await exportDatabase(db);
  } catch (e) {
    console.warn('syncUserSettingsOnly', e);
  }
}

/** Push local notes to Supabase (same upsert as full sync). */
export async function pushUserNotesToRemote(supabase, db, uid) {
  if (!supabase || !db || !uid) {
    return;
  }
  try {
    await pushNotes(supabase, db, uid);
    await exportDatabase(db);
  } catch (e) {
    console.warn('pushUserNotesToRemote', e);
  }
}

/** Push local favorite quotes to Supabase. */
export async function pushUserFavoritesToRemote(supabase, db, uid) {
  if (!supabase || !db || !uid) {
    return;
  }
  try {
    await pushFavorites(supabase, db, uid);
    await exportDatabase(db);
  } catch (e) {
    console.warn('pushUserFavoritesToRemote', e);
  }
}

/** Push local reading plan progress to Supabase. */
export async function pushUserReadingProgressToRemote(supabase, db, uid) {
  if (!supabase || !db || !uid) {
    return;
  }
  try {
    await pushProgress(supabase, db, uid);
    await exportDatabase(db);
  } catch (e) {
    console.warn('pushUserReadingProgressToRemote', e);
  }
}
